/** Major cash equity sessions shown on the Client Reporting dashboard. */
export const MARKET_HOURS = [
  {
    id: 'new-york',
    name: 'New York',
    timeZone: 'America/New_York',
    lat: 40.7128,
    lon: -74.006,
    open: { hour: 9, minute: 30 },
    close: { hour: 16, minute: 0 },
  },
  {
    id: 'london',
    name: 'London',
    timeZone: 'Europe/London',
    lat: 51.5074,
    lon: -0.1278,
    open: { hour: 8, minute: 0 },
    close: { hour: 16, minute: 30 },
  },
  {
    id: 'tokyo',
    name: 'Tokyo',
    timeZone: 'Asia/Tokyo',
    lat: 35.6762,
    lon: 139.6503,
    open: { hour: 9, minute: 0 },
    close: { hour: 15, minute: 0 },
  },
  {
    id: 'sydney',
    name: 'Sydney',
    timeZone: 'Australia/Sydney',
    lat: -33.8688,
    lon: 151.2093,
    open: { hour: 10, minute: 0 },
    close: { hour: 16, minute: 0 },
  },
];

const WEEKEND = new Set(['Sat', 'Sun']);

function getZonedParts(date, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  const parts = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  return {
    weekday: parts.weekday,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

function getLocalYmd(date, timeZone) {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(date);
}

/** Convert a local date/time in `timeZone` to a UTC Date (Intl-only, no deps). */
function utcFromLocalDateTime(timeZone, ymd, hour, minute) {
  const [y, m, d] = ymd.split('-').map(Number);
  let lo = Date.UTC(y, m - 1, d - 1, 0, 0);
  let hi = Date.UTC(y, m - 1, d + 1, 23, 59);
  const target = hour * 60 + minute;

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const dt = new Date(mid);
    const zymd = getLocalYmd(dt, timeZone);
    const zp = getZonedParts(dt, timeZone);
    const mins = zp.hour * 60 + zp.minute;
    if (zymd < ymd || (zymd === ymd && mins < target)) lo = mid + 1;
    else hi = mid;
  }
  return new Date(lo);
}

function isWeekdayOpen(now, market) {
  const p = getZonedParts(now, market.timeZone);
  if (WEEKEND.has(p.weekday)) return false;
  const mins = p.hour * 60 + p.minute;
  const open = market.open.hour * 60 + market.open.minute;
  const close = market.close.hour * 60 + market.close.minute;
  return mins >= open && mins < close;
}

function nextOpen(now, market) {
  const ymdToday = getLocalYmd(now, market.timeZone);
  const p = getZonedParts(now, market.timeZone);
  const openMins = market.open.hour * 60 + market.open.minute;
  const nowMins = p.hour * 60 + p.minute;

  if (!WEEKEND.has(p.weekday) && nowMins < openMins) {
    return utcFromLocalDateTime(
      market.timeZone,
      ymdToday,
      market.open.hour,
      market.open.minute,
    );
  }

  let probe = utcFromLocalDateTime(market.timeZone, ymdToday, 12, 0);
  for (let day = 0; day < 10; day += 1) {
    probe = new Date(probe.getTime() + 86400000);
    const ymd = getLocalYmd(probe, market.timeZone);
    const wd = getZonedParts(probe, market.timeZone).weekday;
    if (!WEEKEND.has(wd)) {
      return utcFromLocalDateTime(
        market.timeZone,
        ymd,
        market.open.hour,
        market.open.minute,
      );
    }
  }
  return null;
}

function nextClose(now, market) {
  const ymd = getLocalYmd(now, market.timeZone);
  const close = utcFromLocalDateTime(
    market.timeZone,
    ymd,
    market.close.hour,
    market.close.minute,
  );
  return close > now ? close : null;
}

export function formatMarketCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}h, ${m}m, ${s}s`;
}

/** Equirectangular projection → % position on the world map image. */
export function marketMapPosition(lat, lon) {
  return {
    left: ((lon + 180) / 360) * 100,
    top: ((90 - lat) / 180) * 100,
  };
}

export function getMarketHourStatus(now, market) {
  const open = isWeekdayOpen(now, market);
  const target = open ? nextClose(now, market) : nextOpen(now, market);
  const ms = target ? Math.max(0, target.getTime() - now.getTime()) : 0;
  const mapPos = marketMapPosition(market.lat, market.lon);

  return {
    id: market.id,
    name: market.name,
    mapLeft: mapPos.left,
    mapTop: mapPos.top,
    isOpen: open,
    countdown: formatMarketCountdown(ms),
    statusText: open ? `closes in ${formatMarketCountdown(ms)}` : `opens in ${formatMarketCountdown(ms)}`,
  };
}

export function getAllMarketHourStatuses(now = new Date()) {
  return MARKET_HOURS.map((market) => getMarketHourStatus(now, market));
}

/** 0–100 position for the solar-noon line on the equirectangular map (matches marketlog-style maps). */
export function getSolarTimelinePercent(now = new Date()) {
  const utcHours = now.getUTCHours() + now.getUTCMinutes() / 60 + now.getUTCSeconds() / 3600;
  // Subsolar longitude: where the sun is overhead (12:00 UTC → 0°, shifts 15°/hour).
  let lon = (12 - utcHours) * 15;
  if (lon < -180) lon += 360;
  if (lon > 180) lon -= 360;
  return ((lon + 180) / 360) * 100;
}

/** @deprecated use getSolarTimelinePercent */
export function getUtcTimelinePercent(now = new Date()) {
  return getSolarTimelinePercent(now);
}
